"use client"

import { useRef, useState, useEffect } from "react"
import { Input } from "../../ui/input"
import { Button } from "../../ui/button"
import { toast } from "sonner"
import { post } from "../../../lib/api"
import { readFileWithEncodingDetection } from "../../../lib/encoding-utils"

const HorseJumpUploader = ({ game_id, onUpdate }) => {
    const [words, setWords] = useState([])
    const [currentWordIndex, setCurrentWordIndex] = useState(0)
    const [currentPathIndex, setCurrentPathIndex] = useState(0)
    const [allPaths, setAllPaths] = useState([])
    const [manualWord, setManualWord] = useState("")
    const [isManualMode, setIsManualMode] = useState(false)
    const [scheduledDate, setScheduledDate] = useState("")
    const [lastUploadedDate, setLastUploadedDate] = useState(null)
    const fileInputRef = useRef(null)

    // Initialize with today's date
    useEffect(() => {
        const today = new Date()
        const todayString = today.toISOString().split("T")[0]
        setScheduledDate(todayString)
    }, [])

    // Update default date after upload
    useEffect(() => {
        if (lastUploadedDate) {
            const nextDay = new Date(lastUploadedDate)
            nextDay.setDate(nextDay.getDate() + 1)
            const nextDayString = nextDay.toISOString().split("T")[0]
            setScheduledDate(nextDayString)
        }
    }, [lastUploadedDate])

    // 3x3 grid positions (middle is empty)
    // 0 1 2
    // 3 X 4
    // 5 6 7
    const positions = [
        [0, 0],
        [0, 1],
        [0, 2], // 0, 1, 2
        [1, 0],
        [1, 2], // 3, 4
        [2, 0],
        [2, 1],
        [2, 2], // 5, 6, 7
    ]

    // Knight moves: [row_offset, col_offset]
    const knightMoves = [
        [-2, -1],
        [-2, 1],
        [-1, -2],
        [-1, 2],
        [1, -2],
        [1, 2],
        [2, -1],
        [2, 1],
    ]

    const getRandomPathIndex = () => {
        return Math.floor(Math.random() * allPaths.length)
    }

    // Find all valid knight tour paths
    useEffect(() => {
        const findAllPaths = () => {
            const paths = []

            const getValidMoves = (posIndex) => {
                if (posIndex < 0 || posIndex >= 8) return []

                const currentPos = positions[posIndex]
                const validMoves = []

                for (const [dr, dc] of knightMoves) {
                    const newRow = currentPos[0] + dr
                    const newCol = currentPos[1] + dc

                    // Check if new position is valid and not the center
                    if (newRow >= 0 && newRow <= 2 && newCol >= 0 && newCol <= 2 && !(newRow === 1 && newCol === 1)) {
                        // Find the index of this position
                        const newIndex = positions.findIndex((pos) => pos[0] === newRow && pos[1] === newCol)
                        if (newIndex !== -1) {
                            validMoves.push(newIndex)
                        }
                    }
                }

                return validMoves
            }

            const dfs = (path, visited) => {
                if (path.length === 8) {
                    paths.push([...path])
                    return
                }

                const currentPos = path[path.length - 1]
                const validMoves = getValidMoves(currentPos)

                for (const nextPos of validMoves) {
                    if (!visited.has(nextPos)) {
                        path.push(nextPos)
                        visited.add(nextPos)
                        dfs(path, visited)
                        path.pop()
                        visited.delete(nextPos)
                    }
                }
            }

            // Try starting from each position
            for (let startPos = 0; startPos < 8; startPos++) {
                dfs([startPos], new Set([startPos]))
            }

            return paths
        }

        setAllPaths(findAllPaths())
    }, [])

    const handleFileChange = async (e) => {
        const file = e.target.files?.[0]
        if (!file || file.type !== "text/plain") {
            toast.error("Please upload a valid .txt file.")
            return
        }

        try {
            const text = await readFileWithEncodingDetection(file)
            const lines = text
                .split("\n")
                .map((line) => line.trim().toUpperCase())
                .filter((line) => line.length === 8 && /^[A-Z]+$/.test(line))

            if (lines.length === 0) {
                toast.error("No valid 8-letter words found in the file.")
                return
            }

            setWords(lines)
            setCurrentWordIndex(0)
            setCurrentPathIndex(getRandomPathIndex())
            setIsManualMode(false)
            toast.success(`Loaded ${lines.length} valid 8-letter words!`)
        } catch (error) {
            toast.error("Failed to read file. Please try again.")
            console.error("File reading error:", error)
        }
    }

    const handleManualWordSubmit = () => {
        const word = manualWord.trim().toUpperCase()

        if (word.length !== 8) {
            toast.error("Please enter exactly 8 letters!")
            return
        }

        if (!/^[A-Z]+$/.test(word)) {
            toast.error("Please enter only letters!")
            return
        }

        setWords([word])
        setCurrentWordIndex(0)
        setCurrentPathIndex(getRandomPathIndex())
        setIsManualMode(true)
        toast.success(`Word "${word}" loaded successfully!`)
    }

    const getCurrentWord = () => words[currentWordIndex] || ""

    const getCurrentPuzzle = () => {
        if (!getCurrentWord() || allPaths.length === 0) return ""

        const currentPath = allPaths[currentPathIndex]
        const word = getCurrentWord()

        // Create puzzle by arranging letters according to the path
        const puzzle = new Array(8)
        for (let i = 0; i < 8; i++) {
            puzzle[currentPath[i]] = word[i]
        }

        return puzzle.join("")
    }

    const getGridDisplay = () => {
        if (!getCurrentWord() || allPaths.length === 0) {
            return Array(9).fill("")
        }

        const currentPath = allPaths[currentPathIndex]
        const word = getCurrentWord()
        const grid = Array(9).fill("")

        // Fill grid positions (skip index 4 which is center)
        for (let i = 0; i < 8; i++) {
            const gridIndex = currentPath[i] < 4 ? currentPath[i] : currentPath[i] + 1
            grid[gridIndex] = word[i]
        }

        grid[4] = "" // Center - empty for now, can be replaced with horse image
        return grid
    }

    const nextWord = () => {
        if (currentWordIndex < words.length - 1) {
            setCurrentWordIndex(currentWordIndex + 1)
            setCurrentPathIndex(getRandomPathIndex())
        }
    }

    const prevWord = () => {
        if (currentWordIndex > 0) {
            setCurrentWordIndex(currentWordIndex - 1)
            setCurrentPathIndex(getRandomPathIndex())
        }
    }

    const formatDateForDisplay = (dateString) => {
        const date = new Date(dateString)
        return date.toLocaleDateString("en-US", {
            weekday: "long",
            year: "numeric",
            month: "long",
            day: "numeric",
        })
    }

    const handleUploadPuzzle = async () => {
        const word = getCurrentWord()
        const puzzle = getCurrentPuzzle()

        if (!word || !puzzle) {
            toast.error("No valid puzzle to upload.")
            return
        }

        if (!scheduledDate) {
            toast.error("Please select a date for the challenge.")
            return
        }

        try {
            // First API call - Add the challenge
            const response = await post(
                {
                    method: "addChallengeNoDup",
                    game_id,
                    challenge_id: word, // Solution is the challenge name
                    data: puzzle, // Scrambled letters is the challenge data
                    variant_id: 1,
                    variant_name: "challenge",
                },
                "/challenges",
            )

            if (response.error) {
                if (response.error === "Failed to fetch") {
                    toast.error("A challenge with that name already exists.")
                } else {
                    toast.error(response.error)
                }
                return
            }

            // Second API call - Schedule the daily challenge
            const dailyResponse = await post(
                {
                    method: "addDailyChallenge",
                    game_id,
                    date: scheduledDate,
                    daily_challenge_info: word, // Use the word (solution) as the daily challenge info
                },
                "/challenges",
            )

            if (dailyResponse.error) {
                toast.error(`Challenge uploaded but scheduling failed: ${dailyResponse.error}`)
                return
            }

            toast.success(`Puzzle "${word}" uploaded and scheduled for ${formatDateForDisplay(scheduledDate)}!`)
            setLastUploadedDate(scheduledDate) // Update the last uploaded date
            onUpdate?.()
        } catch (err) {
            toast.error("Upload failed!")
            console.error(err)
        }
    }

    const gridDisplay = getGridDisplay()

    return (
        <div className="space-y-6 p-8 border rounded-lg w-full min-w-[800px] max-w-6xl mx-auto bg-white">
            {/* Input Options */}
            <div className="space-y-4">
                {/* File Upload */}
                <div className="flex items-center gap-4">
                    <Input type="file" accept=".txt" ref={fileInputRef} style={{ display: "none" }} onChange={handleFileChange} />
                    <Button
                        className="bg-blue-600 hover:bg-blue-700 text-white border border-blue-700 px-6 py-3 font-semibold shadow-md"
                        onClick={() => fileInputRef.current?.click()}
                    >
                        Upload 8-Letter Words File
                    </Button>
                    <span className="text-gray-500">or</span>
                </div>

                {/* Manual Word Entry */}
                <div className="flex items-center gap-4">
                    <Input
                        type="text"
                        placeholder="Enter 8-letter word manually"
                        value={manualWord}
                        onChange={(e) => setManualWord(e.target.value.toUpperCase())}
                        maxLength={8}
                        className="max-w-xs text-black placeholder-gray-500"
                        onKeyPress={(e) => e.key === "Enter" && handleManualWordSubmit()}
                    />
                    <Button
                        className="bg-green-600 hover:bg-green-700 text-white border border-green-700 px-6 py-3 font-semibold shadow-md"
                        onClick={handleManualWordSubmit}
                    >
                        Load Word
                    </Button>
                </div>
            </div>

            {words.length > 0 && (
                <>
                    {/* Word Navigation - Only show if not in manual mode or if manual mode has multiple words */}
                    {(!isManualMode || words.length > 1) && (
                        <div className="flex items-center justify-between p-4 bg-gray-50 rounded-lg border">
                            <Button
                                variant="outline"
                                className="border-gray-400 text-gray-700 hover:bg-gray-100 hover:text-gray-900 px-4 py-2 font-medium shadow-sm"
                                onClick={prevWord}
                                disabled={currentWordIndex === 0}
                            >
                                Previous Word
                            </Button>
                            <div className="text-center">
                                <div className="font-bold text-lg text-black">
                                    Word {currentWordIndex + 1} of {words.length}: {getCurrentWord()}
                                </div>
                                <div className="text-sm text-gray-600">
                                    Solution: {getCurrentWord()} | Puzzle: {getCurrentPuzzle()}
                                </div>
                            </div>
                            <Button
                                variant="outline"
                                className="border-gray-400 text-gray-700 hover:bg-gray-100 hover:text-gray-900 px-4 py-2 font-medium shadow-sm"
                                onClick={nextWord}
                                disabled={currentWordIndex === words.length - 1}
                            >
                                Next Word
                            </Button>
                        </div>
                    )}

                    {/* Manual mode single word display */}
                    {isManualMode && words.length === 1 && (
                        <div className="text-center p-4 bg-gray-50 rounded-lg border">
                            <div className="font-bold text-lg text-black">Current Word: {getCurrentWord()}</div>
                            <div className="text-sm text-gray-600">
                                Solution: {getCurrentWord()} | Puzzle: {getCurrentPuzzle()}
                            </div>
                        </div>
                    )}

                    {/* Grid Display */}
                    <div className="flex justify-center py-6">
                        <div className="grid grid-cols-3 gap-4 p-8 bg-white border-2 rounded-xl shadow-lg">
                            {gridDisplay.map((letter, index) => (
                                <div
                                    key={index}
                                    className={`
          w-20 h-20 flex items-center justify-center text-2xl font-bold border-2 rounded-lg shadow-sm
          ${
                                        index === 4
                                            ? "bg-amber-100 border-amber-300 relative"
                                            : letter
                                                ? "bg-blue-100 border-blue-300 text-blue-800"
                                                : "bg-gray-50 border-gray-200 text-gray-400"
                                    }
        `}
                                >
                                    {index === 4 ? (
                                        // Center square - you can replace this with an image
                                        <div className="text-amber-600 text-4xl">♞</div>
                                        // To use an image instead, uncomment below and provide the image:
                                        // <img src="/path-to-your-horse-image.png" alt="Chess Horse" className="w-12 h-12" />
                                    ) : (
                                        letter
                                    )}
                                </div>
                            ))}
                        </div>
                    </div>

                    {/* Puzzle Info */}
                    <div className="p-4 bg-yellow-50 border border-yellow-200 rounded-lg">
                        <div className="text-base text-black space-y-1">
                            <div>
                                <strong>Original Word:</strong> {getCurrentWord()}
                            </div>
                            <div>
                                <strong>Puzzle Format:</strong> {getCurrentPuzzle()}
                            </div>
                        </div>
                    </div>

                    {/* Date Scheduling */}
                    <div className="p-4 bg-blue-50 border border-blue-200 rounded-lg">
                        <div className="space-y-3">
                            <label htmlFor="scheduledDate" className="block text-sm font-medium text-black">
                                Schedule Challenge Date:
                            </label>
                            <div className="flex items-center gap-4">
                                <Input
                                    type="date"
                                    id="scheduledDate"
                                    value={scheduledDate}
                                    onChange={(e) => setScheduledDate(e.target.value)}
                                    className="max-w-xs text-black"
                                />
                                <div className="text-sm text-gray-600">
                                    {scheduledDate && `Will be scheduled for: ${formatDateForDisplay(scheduledDate)}`}
                                </div>
                            </div>
                            {lastUploadedDate && (
                                <div className="text-xs text-gray-500">
                                    Last upload was scheduled for {formatDateForDisplay(lastUploadedDate)}
                                </div>
                            )}
                        </div>
                    </div>

                    {/* Upload Button */}
                    <Button
                        className="w-full bg-green-600 hover:bg-green-700 text-white border border-green-700 py-3 font-semibold shadow-md"
                        onClick={handleUploadPuzzle}
                    >
                        Schedule This Puzzle for {scheduledDate ? formatDateForDisplay(scheduledDate) : "Selected Date"}
                    </Button>
                </>
            )}

            {words.length === 0 && (
                <div className="text-center text-gray-700 py-8">
                    Upload a .txt file with 8-letter words or enter a word manually to get started
                </div>
            )}
        </div>
    )
}

export default HorseJumpUploader